/**
 * DocumentActivityLog — User-facing log of all document actions across modules.
 * Shows: POs, Requisitions, Invoices, Assets, Sales Orders events.
 * Filterable by module, user, search text, and date range.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { Search, Filter, ChevronLeft, ChevronRight, Activity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MODULE_COLORS = {
  Orders: 'bg-blue-100 text-blue-700',
  Requisitions: 'bg-violet-100 text-violet-700',
  Invoices: 'bg-amber-100 text-amber-700',
  Assets: 'bg-emerald-100 text-emerald-700',
  SalesOrders: 'bg-cyan-100 text-cyan-700',
};

export default function DocumentActivityLog() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [data, setData] = useState([]);
  const [modules, setModules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (searchDebounce) params.search = searchDebounce;
      if (moduleFilter) params.module = moduleFilter;
      if (userFilter) params.user = userFilter;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();

      const res = await api.get('/api/system/document-activity', { params });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      if (res.data.modules?.length) setModules(res.data.modules);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [page, pageSize, searchDebounce, moduleFilter, userFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / pageSize);

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) +
        ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz });
    } catch { return ts; }
  };

  return (
    <AppShell>
      <PageHeader title="Document Activity Log"
        subtitle={`${total} activities found`}
        icon={<Activity className="h-5 w-5 text-primary" />} />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="activity-filters">
        <div className="relative flex-1 min-w-[200px] max-w-[350px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents, users, events..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 h-9 text-sm"
            data-testid="activity-search" />
        </div>
        <select value={moduleFilter} onChange={e => { setModuleFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm" data-testid="activity-module-filter">
          <option value="">All Modules</option>
          {modules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <Input type="text" placeholder="Filter by user..."
          value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }}
          className="h-9 w-[160px] text-sm" data-testid="activity-user-filter" />
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">From:</span>
          <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="h-9 w-[140px] text-sm" data-testid="activity-date-from" />
          <span className="text-xs text-muted-foreground">To:</span>
          <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="h-9 w-[140px] text-sm" data-testid="activity-date-to" />
        </div>
        {(moduleFilter || userFilter || dateFrom || dateTo || search) && (
          <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setModuleFilter(''); setUserFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}
            className="h-9 text-xs" data-testid="activity-clear-filters">
            <Filter className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : data.length === 0 ? (
        <EmptyState title="No activity found" />
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="activity-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[170px]">Date & Time</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[120px]">Module</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[130px]">Document</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[140px]">User</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Activity</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => {
                const color = MODULE_COLORS[item.module] || 'bg-gray-100 text-gray-700';
                return (
                  <tr key={i} className="border-b hover:bg-muted/30 transition-colors" data-testid="activity-row">
                    <td className="px-4 py-2 text-sm whitespace-nowrap text-muted-foreground">{fmtDate(item.timestamp)}</td>
                    <td className="px-4 py-2">
                      <Badge className={`${color} text-[10px] font-semibold`}>{item.module_label}</Badge>
                    </td>
                    <td className="px-4 py-2 text-sm font-mono font-semibold text-primary">{item.document_ref || '—'}</td>
                    <td className="px-4 py-2 text-sm">{item.user || '—'}</td>
                    <td className="px-4 py-2 text-sm">{item.event || item.description || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} ({total} activities)
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              data-testid="activity-prev-page">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              data-testid="activity-next-page">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
